import { ProductListStyles } from '@components/ProductList/styles';
import type { ProductListAPIResponse } from '@partful/shared-types';
import { Job } from '@providers/job/types';
import { usePermissions } from 'src/hooks/usePermissions';
import { filterJobsByProcessingState, getJobsWithRedirectURLs } from './jobFiltering';
import CreateNewProductCard from './ui/CreateNewProductCard';
import InProgressCard from './ui/InProgressCard';
import ProcessingCard from './ui/ProcessingCard';
import ProcessingFailedCard from './ui/ProcessingFailedCard';
import { ProductCard } from './ui/ProductCard';

interface ProductListProps {
  configurations: ProductListAPIResponse;
  jobs?: Job[];
  isWithinCategory?: boolean;
}

export default function ProductList({ configurations, jobs, isWithinCategory }: Readonly<ProductListProps>) {
  const showAddProductTile = usePermissions(['add', 'product']);
  const showProcessingProducts = usePermissions(['update', 'product']);
  const showCreateProductTile = showAddProductTile && !isWithinCategory;

  const jobsWithRedirectURLS = getJobsWithRedirectURLs(jobs ?? []);
  const { inProgressJobs, processingJobs, failedProcessingJobs } = filterJobsByProcessingState(jobsWithRedirectURLS);

  return (
    <ProductListStyles>
      {showCreateProductTile && <CreateNewProductCard products={configurations.length > 0} />}
      <>
        {configurations
          /* istanbul ignore next - covered in a cypress test but we also want to move this logic to the backend  */
          .filter((configuration) => configuration.status?.partTree === 'COMPLETED')
          .map((configuration) => (
            <ProductCard key={configuration.productId} product={configuration} />
          ))}
        {showProcessingProducts && (
          <>
            {inProgressJobs.map(({ job, redirectURL }) => (
              <InProgressCard key={job.runID} job={job} redirectURL={redirectURL} />
            ))}
            {processingJobs.map(({ job, redirectURL }) => (
              <ProcessingCard key={job.runID} job={job} redirectURL={redirectURL} />
            ))}
            {failedProcessingJobs.map(({ job, redirectURL }) => (
              <ProcessingFailedCard
                key={job.runID}
                job={job}
                redirectURL={redirectURL}
                // can only delete if the there isn't a configuration (card) with the same configurationPID as this job
                // otherwise delete via the configurations's managed product page
                canDelete={!configurations.some(({ configurationPID }) => configurationPID === job.configurationPID)}
              />
            ))}
          </>
        )}
      </>
    </ProductListStyles>
  );
}
